import React, { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Quicklink } from '@node-space/storybook-components/dist/Quicklink'
import { Box, Button, Callout, Checkbox, Input2FA } from '@node-space/ui-components'
import { TWO_FA_REQUIRED_LENGTH } from 'components/TwoFA/TwoFAField'
import { BVNK_BLOG_FRAUD_PREVENTION } from 'constants/General'
import { useAddBeneficiaryContext } from 'hooks/useAddBeneficiaryContext'
import { AddLocalBeneficiaryFormConfirmPayload } from 'types/beneficiaries'
import { Currencies } from 'types/types'
import { BeneficiarySummary } from './BeneficiarySummary'
import { addBeneficiaryConfirmSchema } from './schemas/addBeneficiaryConfirmSchema'

type AddBeneficiaryConfirmProps = {
  onContinue: (password: string) => void
  isLoading: boolean
  error: boolean
}

export const AddBeneficiaryConfirm = ({
  onContinue,
  isLoading,
  error,
}: AddBeneficiaryConfirmProps) => {
  const { t } = useTranslation()
  const formSchema = addBeneficiaryConfirmSchema()
  const { control, register, setValue, setError, clearErrors } =
    useForm<AddLocalBeneficiaryFormConfirmPayload>({
      resolver: yupResolver(formSchema),
    })
  const { beneficiaryFormState } = useAddBeneficiaryContext()
  const isGBPBeneficiary = beneficiaryFormState?.currencyCode === Currencies.GBP

  const [password, isDetailsCorrect] = useWatch({
    control,
    name: ['password', 'isDetailsCorrect'],
    defaultValue: {
      password: '',
      isDetailsCorrect: false,
    },
  })
  const { beneficiaryFormErrors } = useAddBeneficiaryContext()

  useEffect(() => {
    if (beneficiaryFormErrors?.password) {
      setError('password', beneficiaryFormErrors?.password)
    } else {
      clearErrors()
    }
  }, [setError, clearErrors, beneficiaryFormErrors?.password])

  useEffect(() => {
    if (password?.length === TWO_FA_REQUIRED_LENGTH) {
      onContinue(password)
    }
  }, [password])

  return (
    <Box flex flexCol padding={24} paddingT={20}>
      <BeneficiarySummary />

      {isGBPBeneficiary && (
        <>
          <Box paddingT={24} paddingB={8}>
            <Callout
              variant="info"
              GhostButton={
                <Button onClick={() => window.open(BVNK_BLOG_FRAUD_PREVENTION, '_blank')}>
                  {t('howToPaySafelyMessage')}
                </Button>
              }
            >
              {t('authorisedPushPaymentsMessage')}
            </Callout>
          </Box>
          <Box paddingT={12} paddingB={8}>
            <Checkbox
              {...register('isDetailsCorrect')}
              id="beneficiary-checkbox"
              testId="beneficiary-checkbox"
              checked={isDetailsCorrect}
              onCheckedChange={() => setValue('isDetailsCorrect', !isDetailsCorrect)}
              label={t('authorisedPushPaymentsConfirmationMessage')}
            >
              <Quicklink
                className="whitespace-nowrap"
                text={t('learnMore')}
                to={BVNK_BLOG_FRAUD_PREVENTION}
              />
            </Checkbox>
          </Box>
        </>
      )}

      <Box paddingT={24} paddingB={12}>
        <Input2FA
          {...register('password')}
          label={t('authCodeConfirm')}
          onChange={value => setValue('password', value)}
          testId="input-2fa"
          error={error && t('incorrectOtpCode')}
          disabled={!isDetailsCorrect && isGBPBeneficiary}
          loading={isLoading}
        />
      </Box>
    </Box>
  )
}
